import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { fontColorDarkTheme } from '../../../styles/global.styles';
import { Checkbox, IconButton } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import get from 'lodash/get';
import * as React from 'react';
import { Dispatch, ReactElement, SetStateAction, useState } from 'react';
import ExpandRowContent from './ExpandRowContent';
import { IColumnProps } from '../index';

interface TableBodyContentProps {
  row: any;
  columns: IColumnProps[];
  index: number;
  isExpandedRow?: boolean;
  onRowClick?: (item: any, index: number) => void;
  expandedRowContent?: ReactElement;
  isEnableSelection?: boolean;
  selected: readonly number[];
  setSelected: Dispatch<SetStateAction<readonly number[]>>;
  fieldToBlockedToSelect?: string;
}

const TableBodyContent = ({
  row,
  index,
  isExpandedRow,
  columns,
  expandedRowContent,
  onRowClick,
  isEnableSelection,
  selected,
  setSelected,
  fieldToBlockedToSelect,
}: TableBodyContentProps) => {
  const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);

  const onTableRowClick = (event: React.MouseEvent<unknown>, id: number) => {
    if (onRowClick) onRowClick(row, index);
    else setIsRowExpanded(!isRowExpanded);

    if (
      (isEnableSelection && !fieldToBlockedToSelect) ||
      (fieldToBlockedToSelect && !row[fieldToBlockedToSelect])
    )
      toSelectItem(event, id);
  };

  const isItemSelected = selected.includes(row.id ?? row.userId);

  const toSelectItem = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  return (
    <>
      <TableRow
        hover
        role="checkbox"
        onClick={(event) => onTableRowClick(event, row.id ?? row.userId)}
        style={{
          border: 'none',
          cursor: onRowClick ? 'pointer' : 'default',
        }}
        selected={isItemSelected}
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={index}
      >
        {isEnableSelection && (
          <TableCell style={{ border: '1px solid var(--app-table-border)' }}>
            <Checkbox
              color="primary"
              checked={isItemSelected}
              disabled={
                (fieldToBlockedToSelect && row[fieldToBlockedToSelect]) || false
              }
            />
          </TableCell>
        )}
        {isExpandedRow && (
          <TableCell
            style={{
              width: 48,
              border: '1px solid var(--app-table-border)',
              borderBottom: '1px solid var(--app-table-border)',
              color: fontColorDarkTheme,
            }}
          >
            <IconButton
              aria-label="expand row"
              size="small"
              style={{ color: fontColorDarkTheme }}
              onClick={() => setIsRowExpanded(!isRowExpanded)}
            >
              {isRowExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}
        {columns.map((column: any, keyIndex) => {
          const value = get(row, column.id);
          return (
            <TableCell
              key={keyIndex}
              style={{
                border: '1px solid var(--app-table-border)',
                borderBottom: '1px solid var(--app-table-border)',
                color: fontColorDarkTheme,
              }}
              align={column.align}
            >
              {column.format && typeof value === 'number'
                ? column.format(value)
                : column.render
                  ? column.render(value, index, row)
                  : value}
            </TableCell>
          );
        })}
      </TableRow>
      {isExpandedRow && (
        <ExpandRowContent
          isOpen={isRowExpanded}
          content={expandedRowContent ? expandedRowContent : ''}
          colSpan={columns.length + (isExpandedRow ? 1 : 0) + (isEnableSelection ? 1 : 0)}
        />
      )}
    </>
  );
};

export default TableBodyContent;
